/* eslint-disable react-hooks/exhaustive-deps */
import {Communication} from "types/communication"
import styles from "./DirectChat.module.css"
import cx from "classnames"
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import {ReactComponent as NotificationIcon} from "./assets/notification.svg"
import {ReactComponent as SearchIcon} from "./assets/search.svg"
import {useQuery} from "@tanstack/react-query"
import {handleErrorChat, toastSuccess} from "helpers"
import {chatServiceV2, userServiceV3} from "services"
import {Icon} from "components"
import {CommunicationContext} from "context/CommunicationContext"
import {Dropdown, Menu, Spin} from "antd"
import {Compose, Message, PinnedIcon, PopupPinnedMessages, UserAvatar} from "sections/NewCommunication/shared"
import {useModel, useVisible} from "hooks"
import ChatWsV2 from "sections/NewCommunication/chatWsV2"
import {useChatTypings, useMessageCompose} from "sections/NewCommunication/hooks"
import {TextPreloader} from "components/TextPreloader"
import SearchChat from "./parts/SearchChat"
import RoomTypes = Communication.RoomTypes
import {getOnlineStatus} from "helpers/chat"
import {ReactComponent as MenuIcon} from "./assets/menu.svg"
import {ConfirmPopup} from "uiKit"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {OptInInfo} from "components/ui"
import {Auth} from "types/auth"

type FragmentMessageOptions = {
    lastMessage?: Communication.Message
    firstMessage?: Communication.Message
    hasMoreDataOnPrev: boolean
    hasMoreDataOnNext: boolean
}

const INIT_FRAGMENT_MESSAGE_OPTIONS: FragmentMessageOptions = {
    hasMoreDataOnPrev: false,
    hasMoreDataOnNext: false
}

type Props = {
    hideHeading?: boolean
    readOnly?: boolean
}

export function DirectChat({hideHeading = false, readOnly = false}: Props) {
    const {
        activeRoom,
        extendedInfoVisible,
        roomGroups,
        changeRoom,
        markDepartmentCampusAsRead,
        viewAs,
        noChatActions,
        setFoundMessageId
    } = useContext(CommunicationContext)
    const [messages, setMessages] = useState<Communication.Message[]>([])
    const [roomMembers, setRoomMembers] = useState<Communication.RoomUser[]>([])
    const {editMessage, replyMessage, onEditMessage, onReplyMessage} = useMessageCompose()
    const messagesRef = useRef<any>()
    const model = useModel()
    const currentUserId = model.user.id
    const searchBarVisible = useVisible(false)
    const fetchingData = useRef<any>()
    const pinnedPopupVisible = useVisible(false)
    const userDateTimeFormat = model.getUserDateTimeFormat()
    const [changedStatus, setChangedStatus] = useState<Communication.SupportRoomStatus>()
    const confirmChangeStatusPopup = useVisible(false)
    const [userInfo, setUserInfo] = useState<Auth.UserProfile | undefined>()

    const {typings} = useChatTypings(+activeRoom.roomId)
    const isTyping = useMemo(() => {
        return Object.keys(typings).filter((i) => parseInt(i) !== currentUserId).length > 0
    }, [typings])

    const isSupportRoom = useMemo(() => {
        return [Communication.RoomTypes.OpenedSupportHub, Communication.RoomTypes.ClosedSupportHub].includes(
            activeRoom.type
        )
    }, [activeRoom])

    const [isFirstIntersecting, setFirstIntersecting] = useState<boolean>(false)
    const [isLastIntersecting, setLastIntersecting] = useState<boolean>(false)
    const firstItem = useRef<any>(null)
    const lastItem = useRef<any>(null)
    const observerFirstItem = new IntersectionObserver(([entry]) => setFirstIntersecting(entry.isIntersecting), {
        threshold: 0.25,
        root: document.querySelector("#chatBody"),
        rootMargin: "150px"
    })
    const observerLastItem = new IntersectionObserver(([entry]) => setLastIntersecting(entry.isIntersecting), {
        threshold: 0.25,
        root: document.querySelector("#chatBody"),
        rootMargin: "150px"
    })
    const [fragmentMessageOptions, setFragmentMessageOptions] =
        useState<FragmentMessageOptions>(INIT_FRAGMENT_MESSAGE_OPTIONS)
    const [highlightMessage, setHighlightMessage] = useState<Communication.Message | undefined>()
    const [unreadFromMessage, setUnreadFromMessage] = useState<Communication.Message | undefined>()
    const [isSecondRequest, setIsSecondRequest] = useState(false)

    useEffect(() => {
        if (firstItem.current) {
            observerFirstItem.observe(firstItem.current)
        }
        return () => {
            observerFirstItem.disconnect()
        }
    }, [observerFirstItem])

    useEffect(() => {
        if (lastItem.current) {
            observerLastItem.observe(lastItem.current)
        }
        return () => {
            observerLastItem.disconnect()
        }
    }, [observerLastItem])

    useEffect(() => {
        if (
            isFirstIntersecting &&
            isSecondRequest &&
            fetchLatestMessageQuery.isFetched &&
            fragmentMessageOptions.hasMoreDataOnPrev
        ) {
            getData(fragmentMessageOptions.firstMessage.messageId, "backward")
        }
    }, [isFirstIntersecting, fragmentMessageOptions, isSecondRequest])

    useEffect(() => {
        if (
            isLastIntersecting &&
            isSecondRequest &&
            fetchLatestMessageQuery.isFetched &&
            fragmentMessageOptions.hasMoreDataOnNext
        ) {
            getData(fragmentMessageOptions.lastMessage.messageId, "forward")
        }
    }, [isLastIntersecting, fragmentMessageOptions, isSecondRequest])

    useEffect(() => {
        if (highlightMessage) {
            setTimeout(() => {
                scrollToMessageId(highlightMessage.messageId)
            }, 1000)
        }
    }, [highlightMessage])

    const scrollToMessageId = (messageId: number) => {
        setFoundMessageId(messageId)
        const el = document.getElementById(`message-${messageId}`)
        el && el.scrollIntoView({block: "start", behavior: "smooth"})
    }

    useEffect(() => {
        setFragmentMessageOptions(INIT_FRAGMENT_MESSAGE_OPTIONS)
        setRoomMembers([])
        setMessages([])
        searchBarVisible.close()
        setUnreadFromMessage(undefined)
        setUserInfo(undefined)

        const unsubscribeNewMessages = ChatWsV2.registerOnNewMessage(activeRoom.roomId, (message) => {
            handlerNewMessage(message)
        })

        const unsubscribeUpdatedMessages = ChatWsV2.registerOnUpdatedMessage(activeRoom.roomId, (message) => {
            handlerUpdatedMessage(message)
        })

        const unsubscribeUpdatedMessagesReadStatus = ChatWsV2.registerOnUpdatedMessageReadStatus(
            activeRoom.roomId,
            (data) => {
                handlerUpdatedMessageReadStatus(data)
            }
        )

        const unsubscribeSupportUpdated = ChatWsV2.registerOnSupportUpdated(activeRoom.roomId, (data) => {
            handlerSupportUpdated(data)
        })

        const unsubscribeSmsDelivery = ChatWsV2.registerOnUpdateMessageSmsDelivery(activeRoom.roomId, (data) => {
            handlerUpdateMessageSmsDelivery(data)
        })

        return () => {
            unsubscribeNewMessages()
            unsubscribeUpdatedMessages()
            unsubscribeUpdatedMessagesReadStatus()
            unsubscribeSupportUpdated()
            unsubscribeSmsDelivery()
        }
    }, [activeRoom.roomId])

    const navigateToMessage = useCallback(
        (_message: Communication.Message) => {
            if (!messages.find((x) => x.messageId === _message.messageId)) {
                setMessages([_message])
                setFragmentMessageOptions({
                    firstMessage: _message,
                    lastMessage: _message,
                    hasMoreDataOnNext: true,
                    hasMoreDataOnPrev: true
                })
            }
            setHighlightMessage({..._message})
        },
        [messages]
    )

    const resetUnreadCount = useCallback(() => {
        if (activeRoom.type === RoomTypes.Department) {
            const room = activeRoom as Communication.DepartmentRoom
            markDepartmentCampusAsRead(room.departmentId, room.campusId, +room.roomId)
            return
        }
        setUnreadFromMessage(undefined)
    }, [roomGroups, activeRoom])

    const handlerNewMessage = (message: Communication.Message) => {
        if (message.roomId === activeRoom.roomId) {
            setMessages((prev) => [...prev, message])
            const isMe = message.author.userId === currentUserId
            if (isMe) {
                // Current user sent message, mark as read all unread messages
                setUnreadFromMessage(undefined)
                markAsRead(message.messageId)
            } else {
                setUnreadFromMessage((prev) => {
                    if (!prev) return message
                    else return prev
                })
            }
            if (model.windowFocused) {
                setHighlightMessage(message)
            }
        }
    }

    const handlerSupportUpdated = (data: any) => {
        if (data.roomId === activeRoom.roomId) {
            const overwriteRoom = {
                ...data,
                type: Communication.SupportRoomStatusToType[data.status]
            }
            changeRoom(overwriteRoom)
        }
    }

    const handlerUpdatedMessage = (message: Communication.Message) => {
        if (message.roomId === activeRoom.roomId) {
            setMessages((prev) => {
                let _all = [...prev]
                const idx = _all.findIndex((x) => x.messageId === message.messageId)
                if (idx >= 0) {
                    _all[idx] = message
                }
                return _all
            })
        }
    }

    const handlerUpdatedMessageReadStatus = ({
        messageIds,
        roomId,
        isRead
    }: {
        messageIds: number[]
        roomId: string | number
        isRead: boolean
    }) => {
        if (roomId === activeRoom.roomId) {
            setMessages((prev) => {
                let _all = [...prev]
                _all = _all.map((x) => {
                    if (messageIds.includes(x.messageId)) {
                        return {...x, isRead}
                    }
                    return x
                })
                return _all
            })
        }
    }

    const handlerUpdateMessageSmsDelivery = (data: {
        roomId: number
        messageId: number
        recipientUserId: number
        smsSid: string | null
        smsStatus: string
    }) => {
        if (Number(data.roomId) !== Number(activeRoom.roomId)) {
            return
        }
        setMessages((prev) =>
            prev.map((msg) => {
                if (msg.messageId !== data.messageId) {
                    return msg
                }
                const existing = msg.smsDelivery ?? []
                const idx = existing.findIndex((d) => d.recipientUserId === data.recipientUserId)
                const entry: Communication.MessageSmsDelivery = {
                    recipientUserId: data.recipientUserId,
                    smsSid: data.smsSid,
                    smsStatus: data.smsStatus,
                    updatedAt: new Date().toISOString()
                }
                const next = [...existing]
                if (idx >= 0) {
                    next[idx] = entry
                } else {
                    next.push(entry)
                }
                return {...msg, smsDelivery: next}
            })
        )
    }

    const notificationMenu = (
        <Menu className={styles.dropdownMenu}>
            {Communication.NOTIFICATION_OPTIONS.map((x) => (
                <Menu.Item key={x.id} onClick={() => saveNotificationSeting(x.id)}>
                    {x.name}
                </Menu.Item>
            ))}
        </Menu>
    )

    const saveNotificationSeting = async (_value: Communication.NotificationMode) => {
        const payload = {
            roomId: activeRoom.roomId,
            notifications: _value
        }
        try {
            await chatServiceV2.editRoomNotificationsSettings(payload)
            toastSuccess("Updated setting")
        } catch (e) {
            handleErrorChat(e)
        }
    }

    const getData = async (messageId: number, direction: "backward" | "forward") => {
        try {
            if (fetchingData.current && fetchingData.current.fetching) return

            fetchingData.current = {
                fetching: true
            }
            const {data} = await chatServiceV2.getFragmentMessages({
                roomId: activeRoom.roomId,
                direction,
                messageId
            })
            const _messages = (data || []).reverse().filter((x) => x.messageId !== messageId)
            setMessages((prev) => [..._messages, ...prev])
            if (_messages.length) {
                if (direction === "backward") {
                    setFragmentMessageOptions((prev) => ({
                        ...prev,
                        firstMessage: _messages[_messages.length - 1]
                    }))
                }
                if (direction === "forward") {
                    setFragmentMessageOptions((prev) => ({
                        ...prev,
                        lastMessage: _messages[0]
                    }))
                }
            } else {
                if (direction === "backward") {
                    setFragmentMessageOptions((prev) => ({
                        ...prev,
                        hasMoreDataOnPrev: false
                    }))
                }
                if (direction === "forward") {
                    setFragmentMessageOptions((prev) => ({
                        ...prev,
                        hasMoreDataOnNext: false
                    }))
                }
            }
        } catch (err) {
            handleErrorChat(err)
        } finally {
            fetchingData.current = {
                fetching: false
            }
        }
    }

    const fetchLatestMessageQuery = useQuery(
        ["room-lastest", activeRoom.roomId],
        () =>
            chatServiceV2.getLatestMessages({
                roomId: parseInt(activeRoom.roomId as string)
            }),
        {
            onSuccess: (resp) => {
                const _messages = (resp.data || []).reverse()
                setMessages(_messages)
                if (_messages.length) {
                    const lastMessage = _messages[_messages.length - 1]
                    setFragmentMessageOptions((prev) => ({
                        ...prev,
                        firstMessage: _messages[0],
                        lastMessage,
                        hasMoreDataOnNext: _messages.length >= 50,
                        hasMoreDataOnPrev: _messages.length >= 50
                    }))
                    setHighlightMessage(lastMessage)
                    if (activeRoom.type === Communication.RoomTypes.Department && model.isStaffOrAdmin()) {
                        const _unreadFromMessage = _messages
                            .filter((x) => !x.isRead)
                            .sort((a, b) => b.messageId - a.messageId)
                            ?.pop()
                        setUnreadFromMessage(_unreadFromMessage)
                    } else {
                        markAsRead(lastMessage.messageId)
                    }
                    setTimeout(() => {
                        setIsSecondRequest(true)
                    }, 1000)
                }
            },
            onError: (err) => {
                handleErrorChat(err)
            },
            enabled: !!activeRoom.roomId
        }
    )

    useQuery(
        ["room-full-members", activeRoom.roomId],
        () =>
            chatServiceV2.getRoomMembers({
                roomId: parseInt(activeRoom.roomId as string),
                range: {
                    pageSize: UNLIMITED_PAGE_SIZE,
                    page: 1
                }
            }),
        {
            onSuccess: async (resp) => {
                setRoomMembers(resp.data || [])
                const excludeUserId = viewAs?.userId ?? currentUserId
                let userId: number | undefined = undefined
                // Get user if room is direct chat
                if (!activeRoom.isGroup && activeRoom.type === Communication.RoomTypes.DirectChat) {
                    const userIds = (resp.data || []).filter((x) => x.userId !== excludeUserId).map((x) => x.userId)
                    if (userIds.length) {
                        userId = userIds[0]
                    }
                }
                // Or if room is departmental chat
                if (activeRoom.type === Communication.RoomTypes.Department) {
                    userId = (resp.data || []).find((x) => x.isDepartmentChatUser && x.userId !== excludeUserId)?.userId
                }
                if (userId) {
                    const userInfo = await userServiceV3.getUserById({
                        filter: {id: [userId]},
                        linkedObjects: false
                    })
                    setUserInfo(userInfo)
                }
            },
            onError: (err) => {
                handleErrorChat(err)
            },
            enabled: !!activeRoom.roomId && !activeRoom.isGroup && !isSupportRoom
        }
    )

    const markAsRead = (messageId: number) => {
        if (viewAs) return
        const payload = {
            roomId: activeRoom.roomId,
            messageId: messageId,
            direction: "backward"
        }
        ChatWsV2.sendPacket({command: "markAsRead", payload})
        resetUnreadCount()
    }

    const onDeleteMessage = async (messageId: number) => {
        try {
            await chatServiceV2.deleteMessage({
                messageId: parseInt(messageId as unknown as string)
            })
            setMessages((prev) => {
                const _messages = [...prev]
                const idx = _messages.findIndex((x) => x.messageId === messageId)
                if (idx >= 0) {
                    _messages.splice(idx, 1)
                }
                return _messages
            })
        } catch (e) {
            handleErrorChat(e)
        }
    }

    const avatar = useMemo(() => {
        if (activeRoom.type === Communication.RoomTypes.DirectChat) {
            return (activeRoom as Communication.DirectChatRoom).image
        }
        if (activeRoom.type === Communication.RoomTypes.Department) {
            return (activeRoom as Communication.DepartmentRoom).user?.photo
        }
        return null
    }, [activeRoom])

    const departmentalUser = useMemo(() => {
        return (roomMembers || []).find((x) => x.isDepartmentChatUser)
    }, [roomMembers])

    const showSupportStatusConfirmation = async (_status: Communication.SupportRoomStatus) => {
        setChangedStatus(_status)
        confirmChangeStatusPopup.open()
    }

    const confirmChangeStatusDescription = useMemo(() => {
        switch (changedStatus) {
            case Communication.SupportRoomStatus.Open:
                return "Are you sure you want to re-open this support request?"
            case Communication.SupportRoomStatus.Cancelled:
                return "Are you sure you want to cancel this support request?"
            default:
                return "Are you sure you want to confirm this support request has been resolved?"
        }
    }, [changedStatus])

    const changeSupportStatus = async () => {
        try {
            confirmChangeStatusPopup.close()
            await chatServiceV2.updateSupportStatus({
                supportId: (activeRoom as Communication.SupportRoom).supportId,
                status: changedStatus
            })
            toastSuccess("Success")
        } catch (e) {
            handleErrorChat(e)
        }
    }

    const supportMenu = () => {
        const isClosedRequest = activeRoom.type === Communication.RoomTypes.ClosedSupportHub
        return (
            <Menu className={styles.dropdownMenu}>
                <Menu.Item
                    icon={<Icon icon="EDIT_LINE" className={styles.menuIcon} />}
                    onClick={() => {
                        showSupportStatusConfirmation(
                            isClosedRequest
                                ? Communication.SupportRoomStatus.Open
                                : Communication.SupportRoomStatus.Resolved
                        )
                    }}>
                    {isClosedRequest ? "Re-Open this request" : "Confirm as resolved"}
                </Menu.Item>
                {!isClosedRequest && (
                    <Menu.Item
                        icon={<Icon icon="FOLLOW_UP" className={styles.menuIcon} />}
                        onClick={() => {
                            showSupportStatusConfirmation(Communication.SupportRoomStatus.Cancelled)
                        }}>
                        Cancel this request
                    </Menu.Item>
                )}
            </Menu>
        )
    }

    const renderTyping = () => {
        if (isTyping) {
            const _user = roomMembers.find((x) => x.userId === parseInt(Object.keys(typings)[0]))
            if (_user) {
                return (
                    <div className={styles.typingContainer}>
                        <UserAvatar user={_user} className={styles.avatar} />
                        <span className={styles.fullName}>{_user.fullName}</span>
                        <TextPreloader size="small" />
                    </div>
                )
            }
        }
        return null
    }

    const onlineAt = useMemo(() => {
        const _room = activeRoom as any
        let onlineAt: Communication.OnlineStatus = _room.onlineAt
        if (_room.type === Communication.RoomTypes.Department) {
            onlineAt = _room.user?.onlineAt
        }
        return onlineAt
    }, [activeRoom])

    const renderOnlineStatus = () => {
        const _room = activeRoom as any
        if (_room.isGroup || isSupportRoom) return null
        return <span className={styles.onlineStatus}>{getOnlineStatus(onlineAt, userDateTimeFormat)}</span>
    }

    return (
        <div className={styles.containerWrap}>
            <div className={styles.container}>
                {!hideHeading && (
                    <>
                        <div className={cx(styles.heading)}>
                            <div className={cx(styles.titleWrap)}>
                                {activeRoom.isGroup ? (
                                    <Icon
                                        icon="PEOPLE"
                                        className={cx(styles.avatarWrap, {
                                            [styles.hasStudentAvatarWrap]: activeRoom?.jsonData?.hasStudentsMembers
                                        })}
                                    />
                                ) : (
                                    <div className={styles.onlineWrap}>
                                        <img
                                            src={avatar || "/image/DefaultAvatar.png"}
                                            alt=""
                                            className={styles.avatarWrap}
                                        />
                                        {!isSupportRoom && (
                                            <div
                                                className={cx(styles.onlineDot, {
                                                    [styles.offlineDot]: onlineAt !== "online"
                                                })}
                                            />
                                        )}
                                    </div>
                                )}
                                <div className={styles.roomInfoWrap}>
                                    <div className={styles.titleWrap}>
                                        <span className={styles.title}>{activeRoom.name}</span>
                                        {!activeRoom.isGroup && !isSupportRoom && userInfo ? (
                                            <OptInInfo user={userInfo} />
                                        ) : null}
                                    </div>
                                    {renderOnlineStatus()}
                                </div>
                            </div>
                            <div className={styles.roomActionWrap}>
                                {isSupportRoom ? (
                                    <>
                                        <Dropdown overlay={supportMenu} trigger={["click"]} className={styles.iconWrap}>
                                            <MenuIcon className={styles.icon} />
                                        </Dropdown>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            className={styles.iconWrap}
                                            onClick={() => {
                                                if (extendedInfoVisible.isVisible) extendedInfoVisible.close()
                                                searchBarVisible.toggle()
                                            }}>
                                            <SearchIcon className={styles.icon} />
                                        </div>
                                        {!noChatActions && (
                                            <Dropdown
                                                overlay={notificationMenu}
                                                trigger={["click"]}
                                                className={styles.iconWrap}>
                                                <NotificationIcon className={styles.icon} />
                                            </Dropdown>
                                        )}
                                        <PinnedIcon onClick={pinnedPopupVisible.open} roomId={activeRoom.roomId} />
                                        <div
                                            className={styles.iconWrap}
                                            onClick={() => {
                                                extendedInfoVisible.toggle()
                                                if (searchBarVisible.isVisible) searchBarVisible.close()
                                            }}>
                                            <Icon icon="ICON_EXPEND" className={styles.icon} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
                <div className={styles.mainContent}>
                    <div className={styles.chatContent} ref={messagesRef} id="chatBody">
                        <div ref={firstItem} />
                        {messages.map((item, index) => {
                            const isUnread = unreadFromMessage?.messageId === item.messageId

                            return (
                                <div key={item.messageId}>
                                    {isUnread && <div className={styles.separator}>unread</div>}
                                    <Message
                                        message={item}
                                        showOptions={
                                            !readOnly && activeRoom.type !== Communication.RoomTypes.ClosedSupportHub
                                        }
                                        onEditMessage={onEditMessage}
                                        onReplyMessage={onReplyMessage}
                                        onDeleteMessage={onDeleteMessage}
                                        showTimestampSection
                                        prevMessageTime={index > 0 ? messages[index - 1].createdAt : undefined}
                                        highlightStudentMessage={
                                            departmentalUser?.userId === item.author.userId &&
                                            activeRoom.type === Communication.RoomTypes.Department
                                        }
                                        onClickReplyMessage={(_msg) => {
                                            scrollToMessageId(_msg.messageId)
                                        }}
                                        roomMembers={roomMembers}
                                    />
                                </div>
                            )
                        })}
                        {messages.length === 0 && !fetchLatestMessageQuery.isFetching && (
                            <div className={styles.loadMoreText}>No messages found. Let's start a chat!</div>
                        )}
                        <div ref={lastItem} />
                        {fetchLatestMessageQuery.isFetching && (
                            <div className={styles.loading}>
                                <Spin />
                            </div>
                        )}
                    </div>
                </div>
                {renderTyping()}
                {!noChatActions && activeRoom.type !== Communication.RoomTypes.ClosedSupportHub && !readOnly && (
                    <div className={styles.messageBox}>
                        <Compose
                            editMessage={editMessage}
                            replyMessage={replyMessage}
                            onlyContent
                            userList={roomMembers}
                            directParticipantInfo={userInfo}
                        />
                    </div>
                )}
            </div>
            <SearchChat
                roomId={activeRoom.roomId}
                visible={searchBarVisible.isVisible}
                onHideSearch={searchBarVisible.close}
                onClickResult={navigateToMessage}
            />
            {!isSupportRoom && (
                <PopupPinnedMessages
                    roomId={activeRoom.roomId}
                    visible={pinnedPopupVisible.isVisible}
                    onClose={pinnedPopupVisible.close}
                    onClickPinnedMesage={(message) => {
                        setHighlightMessage(message)
                        pinnedPopupVisible.close()
                    }}
                />
            )}
            <ConfirmPopup
                isVisible={confirmChangeStatusPopup.isVisible}
                onClose={confirmChangeStatusPopup.close}
                onConfirm={changeSupportStatus}
                title="Confirm"
                description={confirmChangeStatusDescription}
                icon="QUESTION"
            />
        </div>
    )
}
